// src/component/AdminCotozute.js
// Cotozute マスキング設定管理画面 + ことづて検索
// Firestore: appConfig/cotozuteMasking { requiredPhases: ["4"], enabled: true }
import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Avatar,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Switch,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchIcon from "@mui/icons-material/Search";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import { doc, onSnapshot, setDoc, serverTimestamp, collection, query, orderBy, where, startAt, endAt, limit, startAfter, getDocs } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const SEARCH_PAGE_SIZE = 20;

const DOC_PATH = "appConfig/cotozuteMasking";

// 選択肢として表示する paymentPhase 一覧
const PHASE_OPTIONS = [
  { value: "1", label: "1期（2024）" },
  { value: "2", label: "2期（2025前期）" },
  { value: "3", label: "3期（2025後期）" },
  { value: "4", label: "4期（2026）" },
];

export default function AdminCotozute() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";

  const [loading, setLoading] = useState(true);
  const [remote, setRemote] = useState({ requiredPhases: [], enabled: false, visibleChars: 10, hidePostForm: false });
  const [requiredPhases, setRequiredPhases] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [visibleChars, setVisibleChars] = useState("10");
  const [hidePostForm, setHidePostForm] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  // --- ことづて検索 ---
  const [searchMode, setSearchMode] = useState("email"); // "userName" | "email" | "uid"
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [lastSearchDoc, setLastSearchDoc] = useState(null);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [resolvedUser, setResolvedUser] = useState(null); // メアド検索時に見つかったユーザー情報
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" | "asc" 日付ソート

  // --- コメントダイアログ ---
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentDialogPost, setCommentDialogPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // --- 削除ログ ---
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [logsLastDoc, setLogsLastDoc] = useState(null);
  const [logsHasMore, setLogsHasMore] = useState(false);
  const [logsTypeFilter, setLogsTypeFilter] = useState("all"); // "all" | "post" | "comment"
  const [logsAdminOnly, setLogsAdminOnly] = useState(false);
  const [logsMailFailedOnly, setLogsMailFailedOnly] = useState(false);
  const [logDetail, setLogDetail] = useState(null);

  // Firestore からリアルタイム取得
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "appConfig", "cotozuteMasking"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const phases = Array.isArray(data.requiredPhases) ? data.requiredPhases : [];
        const en = Boolean(data.enabled);
        const vc = typeof data.visibleChars === "number" ? data.visibleChars : 10;
        const hpf = Boolean(data.hidePostForm);
        setRemote({ requiredPhases: phases, enabled: en, visibleChars: vc, hidePostForm: hpf });
        setRequiredPhases(phases);
        setEnabled(en);
        setVisibleChars(String(vc));
        setHidePostForm(hpf);
        setDirty(false);
        setLoading(false);
      },
      (err) => {
        console.error("[AdminCotozute] onSnapshot error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleTogglePhase = (phase) => {
    setRequiredPhases((prev) =>
      prev.includes(phase) ? prev.filter((p) => p !== phase) : [...prev, phase]
    );
    setDirty(true);
    setSaveMsg("");
  };

  const handleToggleEnabled = () => {
    setEnabled((prev) => !prev);
    setDirty(true);
    setSaveMsg("");
  };

  // メアドからUIDを解決する
  const resolveUidFromEmail = async (email) => {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const userData = snap.docs[0].data();
    return { uid: snap.docs[0].id, name: userData.name || userData.displayName || "" };
  };

  const handleSearch = useCallback(async (loadMore = false) => {
    const trimmed = searchText.trim();
    if (!trimmed) return;
    setSearching(true);
    setSearchError("");
    if (!loadMore) {
      setSearchResults([]);
      setLastSearchDoc(null);
      setHasMoreResults(false);
      setResolvedUser(null);
    }
    try {
      const postsRef = collection(db, "mmPosts");
      let constraints;

      if (searchMode === "userName") {
        // ユーザー名で前方一致検索
        constraints = [
          orderBy("userName"),
          startAt(trimmed),
          endAt(trimmed + "\uf8ff"),
          limit(SEARCH_PAGE_SIZE),
        ];
      } else {
        // UID検索 or メアド→UID変換後検索
        let targetUid = trimmed;
        if (searchMode === "email") {
          const user = await resolveUidFromEmail(trimmed);
          if (!user) {
            setSearchError("該当するメールアドレスのユーザーが見つかりません。");
            setSearching(false);
            setSearchedOnce(true);
            return;
          }
          targetUid = user.uid;
          if (!loadMore) setResolvedUser(user);
        }
        constraints = [
          where("userId", "==", targetUid),
          orderBy("createdAt", sortOrder),
          limit(SEARCH_PAGE_SIZE),
        ];
      }

      if (loadMore && lastSearchDoc) {
        constraints.splice(constraints.length - 1, 0, startAfter(lastSearchDoc));
      }
      const q = query(postsRef, ...constraints);
      const snap = await getDocs(q);
      const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // userName モードはサーバー側で userName 順のため、日付ソートはクライアント側で適用
      setSearchResults((prev) => {
        const merged = loadMore ? [...prev, ...posts] : posts;
        if (searchMode === "userName") {
          merged.sort((a, b) => {
            const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
            const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
            return sortOrder === "desc" ? tb - ta : ta - tb;
          });
        }
        return merged;
      });
      setLastSearchDoc(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null);
      setHasMoreResults(snap.size === SEARCH_PAGE_SIZE);
      setSearchedOnce(true);
    } catch (err) {
      console.error("[AdminCotozute] search error:", err);
      setSearchError("検索に失敗しました: " + err.message);
    } finally {
      setSearching(false);
    }
  }, [searchText, searchMode, lastSearchDoc, sortOrder]);

  // 削除ログ取得
  const fetchLogs = useCallback(async (loadMore = false) => {
    setLogsLoading(true);
    setLogsError("");
    try {
      const logsRef = collection(db, "mmPostDeletionLogs");
      const constraints = [orderBy("deletedAt", "desc"), limit(30)];
      if (logsTypeFilter !== "all") {
        constraints.unshift(where("type", "==", logsTypeFilter));
      }
      if (logsAdminOnly) {
        constraints.unshift(where("deletedByAdmin", "==", true));
      }
      if (logsMailFailedOnly) {
        constraints.unshift(where("emailStatus", "==", "failed"));
      }
      if (loadMore && logsLastDoc) {
        constraints.splice(constraints.length - 1, 0, startAfter(logsLastDoc));
      }
      const q = query(logsRef, ...constraints);
      const snap = await getDocs(q);
      const entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLogs((prev) => (loadMore ? [...prev, ...entries] : entries));
      setLogsLastDoc(snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null);
      setLogsHasMore(snap.size === 30);
    } catch (err) {
      console.error("[AdminCotozute] logs fetch error:", err);
      setLogsError("削除ログの取得に失敗しました: " + err.message);
    } finally {
      setLogsLoading(false);
    }
  }, [logsTypeFilter, logsAdminOnly, logsMailFailedOnly, logsLastDoc]);

  // フィルタ変更時に再取得（初回も含む）
  useEffect(() => {
    setLogs([]);
    setLogsLastDoc(null);
    setLogsHasMore(false);
    fetchLogs(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logsTypeFilter, logsAdminOnly, logsMailFailedOnly]);

  // 検索ソート順変更時に、既に検索済みなら先頭から再取得
  useEffect(() => {
    if (!searchedOnce) return;
    handleSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  const handleOpenComments = async (post) => {
    setCommentDialogPost(post);
    setCommentDialogOpen(true);
    setComments([]);
    setLoadingComments(true);
    try {
      const commentsRef = collection(db, "mmPosts", post.id, "comments");
      const q = query(commentsRef, orderBy("createdAt", "desc"), limit(100));
      const snap = await getDocs(q);
      setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("[AdminCotozute] comments error:", err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const parsedVC = Math.max(0, Math.floor(Number(visibleChars) || 0));
      await setDoc(doc(db, "appConfig", "cotozuteMasking"), {
        requiredPhases,
        enabled,
        visibleChars: parsedVC,
        hidePostForm,
        updatedAt: serverTimestamp(),
      });
      setSaveMsg("保存しました");
      setDirty(false);
    } catch (err) {
      console.error("[AdminCotozute] save error:", err);
      setSaveMsg("保存に失敗しました: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 600 }}>
      <Typography
        variant="overline"
        sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
      >
        ADMIN
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase, mb: 0.5 }}
      >
        cotozute関連
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 3 }}>
        指定した paymentPhase を持たないユーザーに対して、投稿者名・投稿内容・返信をマスキング表示します。
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        {/* ON/OFF スイッチ */}
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={handleToggleEnabled}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: accent,
                },
              }}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textBase }}>
              マスキングを有効にする
            </Typography>
          }
          sx={{ mb: 1 }}
        />

        {/* 投稿制限 スイッチ */}
        <FormControlLabel
          control={
            <Switch
              checked={hidePostForm}
              onChange={() => { setHidePostForm((prev) => !prev); setDirty(true); setSaveMsg(""); }}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: accent,
                },
              }}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textBase }}>
              対象外ユーザーの投稿フォームを非表示にする
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {/* paymentPhase 選択 */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: textBase, fontWeight: 700 }}>
          必要な paymentPhase（いずれかを持っていれば表示）
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "rgba(0,0,0,0.5)" }}>
          チェックした paymentPhase のいずれも持たないユーザーに対して、名前と投稿内容がマスキングされます。
        </Typography>

        <FormGroup>
          {PHASE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={requiredPhases.includes(opt.value)}
                  onChange={() => handleTogglePhase(opt.value)}
                  sx={{
                    "&.Mui-checked": { color: accent },
                  }}
                />
              }
              label={opt.label}
            />
          ))}
        </FormGroup>

        {/* 投稿テキストの表示文字数 */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: textBase, fontWeight: 700 }}>
          投稿テキストの表示文字数
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "rgba(0,0,0,0.5)" }}>
          マスキング時に先頭から何文字を表示するか設定します。テキストがこの文字数以下の場合はそのまま表示されます。
        </Typography>
        <TextField
          label="表示文字数"
          type="number"
          size="small"
          value={visibleChars}
          onChange={(e) => { setVisibleChars(e.target.value); setDirty(true); setSaveMsg(""); }}
          inputProps={{ min: 0, max: 100 }}
          sx={{ width: 140 }}
        />

        {/* 現在の設定プレビュー */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: `${accent}08`,
            border: `1px solid ${accent}22`,
          }}
        >
          <Typography variant="body2" sx={{ color: textBase }}>
            現在の設定: マスキング{" "}
            <strong>{remote.enabled ? "ON" : "OFF"}</strong>
            {remote.requiredPhases.length > 0 && (
              <>
                {" "}／ 必要phase:{" "}
                <strong>{remote.requiredPhases.join(", ")}</strong>
              </>
            )}
            {" "}／ 表示文字数: <strong>{remote.visibleChars}</strong>
            {" "}／ 投稿制限: <strong>{remote.hidePostForm ? "ON" : "OFF"}</strong>
          </Typography>
          {dirty && (
            <Typography variant="body2" sx={{ color: accent, mt: 0.5 }}>
              変更後: マスキング <strong>{enabled ? "ON" : "OFF"}</strong>
              {requiredPhases.length > 0 && (
                <>
                  {" "}／ 必要phase: <strong>{requiredPhases.join(", ")}</strong>
                </>
              )}
              {" "}／ 表示文字数: <strong>{Math.max(0, Math.floor(Number(visibleChars) || 0))}</strong>
              {" "}／ 投稿制限: <strong>{hidePostForm ? "ON" : "OFF"}</strong>
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "rgba(0,0,0,0.45)" }}>
          例: 「4期（2026）」をチェック＋表示文字数10にすると、payments配列に paymentPhase "4" が
          含まれていないユーザーには名前が「本＊＊＊＊＊」、投稿内容は先頭10文字だけ表示され残りがマスキングされます。
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />
            }
            disabled={!dirty || saving}
            onClick={handleSave}
            sx={{
              textTransform: "none",
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            }}
          >
            保存
          </Button>
        </Stack>

        {saveMsg && (
          <Alert
            severity={saveMsg.startsWith("保存しました") ? "success" : "error"}
            sx={{ mt: 2 }}
          >
            {saveMsg}
          </Alert>
        )}
      </Paper>

      {/* ───── ことづて検索セクション ───── */}
      <Divider sx={{ my: 4 }} />
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase, mb: 0.5 }}
      >
        ことづて検索
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 2 }}>
        メールアドレス・UID・ユーザー名で投稿を検索します。
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={(_, v) => { if (v) { setSearchMode(v); setSearchResults([]); setSearchedOnce(false); setSearchError(""); setResolvedUser(null); } }}
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="email" sx={{ textTransform: "none" }}>メールアドレス</ToggleButton>
          <ToggleButton value="uid" sx={{ textTransform: "none" }}>UID</ToggleButton>
          <ToggleButton value="userName" sx={{ textTransform: "none" }}>ユーザー名</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            placeholder={
              searchMode === "email" ? "メールアドレスを入力" :
              searchMode === "uid" ? "UIDを入力" :
              "ユーザー名を入力（前方一致）"
            }
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSearch(false); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "rgba(0,0,0,0.4)" }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            disabled={!searchText.trim() || searching}
            onClick={() => handleSearch(false)}
            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{
              textTransform: "none",
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            }}
          >
            検索
          </Button>
        </Stack>

        {searchError && (
          <Alert severity="error" sx={{ mt: 2 }}>{searchError}</Alert>
        )}

        {/* メアド検索時のユーザー情報 */}
        {resolvedUser && (
          <Alert severity="info" sx={{ mt: 2 }}>
            ユーザー: <strong>{resolvedUser.name || "名前なし"}</strong>（UID: {resolvedUser.uid}）
          </Alert>
        )}

        {/* 検索結果 */}
        {searchResults.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                {searchResults.length}件{hasMoreResults ? "+" : ""} の結果
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                日付
              </Typography>
              <ToggleButtonGroup
                value={sortOrder}
                exclusive
                onChange={(_, v) => { if (v) setSortOrder(v); }}
                size="small"
                disabled={searching}
              >
                <ToggleButton value="desc" sx={{ textTransform: "none", py: 0.2, px: 1 }}>新しい順</ToggleButton>
                <ToggleButton value="asc" sx={{ textTransform: "none", py: 0.2, px: 1 }}>古い順</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack spacing={1.5}>
              {searchResults.map((post) => (
                <Paper
                  key={post.id}
                  variant="outlined"
                  onClick={() => handleOpenComments(post)}
                  sx={{
                    p: 2, borderRadius: 2, border: `1px solid ${border}`,
                    cursor: "pointer",
                    transition: "background-color 0.15s",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar
                      src={post.userAvatar || ""}
                      sx={{ width: 36, height: 36, mt: 0.3 }}
                    >
                      {(post.userName || "?")[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textBase }}>
                          {post.userName || "名前なし"}
                        </Typography>
                        {post.prefecture && (
                          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.45)" }}>
                            {post.prefecture}
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.4)" }}>
                          {post.createdAt?.toDate
                            ? post.createdAt.toDate().toLocaleString("ja-JP")
                            : post.dateKey || ""}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, color: textBase, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {post.text}
                      </Typography>
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <FavoriteIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.35)" }} />
                          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                            {post.likeCount || 0}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.35)" }} />
                          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                            {post.commentCount || 0}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {hasMoreResults && (
              <Button
                fullWidth
                variant="outlined"
                disabled={searching}
                onClick={() => handleSearch(true)}
                sx={{ mt: 2, textTransform: "none", borderColor: border, color: textBase }}
              >
                {searching ? <CircularProgress size={18} /> : "もっと読み込む"}
              </Button>
            )}
          </Box>
        )}

        {searchedOnce && searchResults.length === 0 && !searching && !searchError && (
          <Typography variant="body2" sx={{ mt: 2, color: "rgba(0,0,0,0.5)", textAlign: "center" }}>
            該当する投稿が見つかりませんでした。
          </Typography>
        )}
      </Paper>

      {/* ───── 削除ログセクション ───── */}
      <Divider sx={{ my: 4 }} />
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase, mb: 0.5 }}
      >
        削除ログ
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 2 }}>
        投稿・コメントの削除履歴を新しい順に表示します（自己削除も含む）。
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            value={logsTypeFilter}
            exclusive
            onChange={(_, v) => { if (v) setLogsTypeFilter(v); }}
            size="small"
          >
            <ToggleButton value="all" sx={{ textTransform: "none" }}>全て</ToggleButton>
            <ToggleButton value="post" sx={{ textTransform: "none" }}>投稿</ToggleButton>
            <ToggleButton value="comment" sx={{ textTransform: "none" }}>コメント</ToggleButton>
          </ToggleButtonGroup>
          <FormControlLabel
            control={
              <Switch
                checked={logsAdminOnly}
                onChange={() => setLogsAdminOnly((prev) => !prev)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: accent,
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: textBase }}>
                管理者による削除のみ
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={logsMailFailedOnly}
                onChange={() => setLogsMailFailedOnly((prev) => !prev)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "error.main" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "error.main",
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: textBase }}>
                メール送信失敗のみ
              </Typography>
            }
          />
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchLogs(false)}
            disabled={logsLoading}
            sx={{ textTransform: "none", borderColor: border, color: textBase }}
          >
            再読込
          </Button>
        </Stack>

        {logsError && (
          <Alert severity="error" sx={{ mt: 2 }}>{logsError}</Alert>
        )}

        <Box sx={{ mt: 2 }}>
          {logsLoading && logs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : logs.length === 0 ? (
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)", textAlign: "center", py: 3 }}>
              削除ログはまだありません。
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {logs.map((log) => {
                const isPost = log.type === "post";
                const isAdminDel = log.deletedByAdmin && !log.deletedBySelf;
                return (
                  <Paper
                    key={log.id}
                    variant="outlined"
                    onClick={() => setLogDetail(log)}
                    sx={{
                      p: 2, borderRadius: 2, border: `1px solid ${border}`,
                      cursor: "pointer",
                      transition: "background-color 0.15s",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 36, height: 36, borderRadius: "50%",
                          bgcolor: isAdminDel ? "warning.light" : "rgba(0,0,0,0.06)",
                          color: isAdminDel ? "warning.dark" : "rgba(0,0,0,0.6)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isAdminDel ? <ShieldOutlinedIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="caption" sx={{
                            fontWeight: 700,
                            color: isPost ? "primary.main" : "secondary.main",
                            border: 1, borderColor: isPost ? "primary.light" : "secondary.light",
                            borderRadius: 1, px: 0.7, py: 0.1,
                          }}>
                            {isPost ? "投稿" : "コメント"}
                          </Typography>
                          {isAdminDel && (
                            <Typography variant="caption" sx={{
                              fontWeight: 700, color: "warning.dark",
                              border: 1, borderColor: "warning.light",
                              borderRadius: 1, px: 0.7, py: 0.1,
                            }}>
                              管理者削除
                            </Typography>
                          )}
                          {isAdminDel && log.emailStatus === "sent" && (
                            <Typography variant="caption" sx={{
                              fontWeight: 700, color: "success.dark",
                              border: 1, borderColor: "success.light",
                              borderRadius: 1, px: 0.7, py: 0.1,
                            }}>
                              メール送信済み ✓
                            </Typography>
                          )}
                          {isAdminDel && log.emailStatus === "failed" && (
                            <Typography variant="caption" sx={{
                              fontWeight: 700, color: "error.dark",
                              border: 1, borderColor: "error.light",
                              borderRadius: 1, px: 0.7, py: 0.1,
                            }}>
                              送信失敗
                            </Typography>
                          )}
                          {isAdminDel && !log.emailStatus && (
                            <Typography variant="caption" sx={{
                              fontWeight: 700, color: "rgba(0,0,0,0.55)",
                              border: 1, borderColor: "rgba(0,0,0,0.2)",
                              borderRadius: 1, px: 0.7, py: 0.1,
                            }}>
                              メール送信待ち
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.4)" }}>
                            {log.deletedAt?.toDate
                              ? log.deletedAt.toDate().toLocaleString("ja-JP")
                              : ""}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mt: 0.5, color: textBase, fontWeight: 700 }}>
                          {log.targetUserName || "名前なし"}
                          <Typography component="span" variant="caption" sx={{ ml: 1, color: "rgba(0,0,0,0.5)", fontWeight: 400 }}>
                            の{isPost ? "投稿" : "コメント"}を削除
                          </Typography>
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.3,
                            color: "rgba(0,0,0,0.7)",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {log.targetText || "（本文なし）"}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "rgba(0,0,0,0.45)" }}>
                          削除者: {log.deletedByUserName || log.deletedByUserId || "不明"}
                          {log.deletedBySelf ? "（自己削除）" : ""}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}

          {logsHasMore && (
            <Button
              fullWidth
              variant="outlined"
              disabled={logsLoading}
              onClick={() => fetchLogs(true)}
              sx={{ mt: 2, textTransform: "none", borderColor: border, color: textBase }}
            >
              {logsLoading ? <CircularProgress size={18} /> : "もっと読み込む"}
            </Button>
          )}
        </Box>
      </Paper>

      {/* ───── 削除ログ詳細ダイアログ ───── */}
      <Dialog
        open={Boolean(logDetail)}
        onClose={() => setLogDetail(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textBase }}>
            削除ログ詳細
          </Typography>
          <IconButton
            onClick={() => setLogDetail(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {logDetail && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>種別</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {logDetail.type === "post" ? "投稿" : "コメント"}
                  {logDetail.deletedByAdmin && !logDetail.deletedBySelf && "（管理者削除）"}
                  {logDetail.deletedBySelf && "（自己削除）"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>削除日時</Typography>
                <Typography variant="body2">
                  {logDetail.deletedAt?.toDate
                    ? logDetail.deletedAt.toDate().toLocaleString("ja-JP")
                    : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>削除者</Typography>
                <Typography variant="body2">
                  {logDetail.deletedByUserName || "-"}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.4)", fontFamily: "monospace" }}>
                  UID: {logDetail.deletedByUserId || "-"}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>対象ユーザー</Typography>
                <Typography variant="body2">
                  {logDetail.targetUserName || "-"}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.4)", fontFamily: "monospace" }}>
                  UID: {logDetail.targetUserId || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>本文（削除時スナップショット）</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, borderColor: border }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {logDetail.targetText || "（本文なし）"}
                  </Typography>
                </Paper>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {logDetail.targetPrefecture && (
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>都道府県</Typography>
                    <Typography variant="body2">{logDetail.targetPrefecture}</Typography>
                  </Box>
                )}
                {logDetail.targetDateKey && (
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>投稿日</Typography>
                    <Typography variant="body2">{logDetail.targetDateKey}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>いいね数</Typography>
                  <Typography variant="body2">{logDetail.targetLikeCount || 0}</Typography>
                </Box>
                {logDetail.type === "post" && (
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>コメント数</Typography>
                    <Typography variant="body2">{logDetail.targetCommentCount || 0}</Typography>
                  </Box>
                )}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>対象ID</Typography>
                <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", color: "rgba(0,0,0,0.6)" }}>
                  postId: {logDetail.targetPostId || "-"}
                </Typography>
                {logDetail.type === "comment" && (
                  <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace", color: "rgba(0,0,0,0.6)" }}>
                    commentId: {logDetail.targetCommentId || "-"}
                  </Typography>
                )}
              </Box>

              {/* メール送信状況（管理者削除の場合のみ） */}
              {logDetail.deletedByAdmin && !logDetail.deletedBySelf && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
                      通知メール送信状況
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color:
                          logDetail.emailStatus === "sent"
                            ? "success.main"
                            : logDetail.emailStatus === "failed"
                            ? "error.main"
                            : "rgba(0,0,0,0.6)",
                      }}
                    >
                      {logDetail.emailStatus === "sent" && "送信済み ✓"}
                      {logDetail.emailStatus === "failed" && "送信失敗"}
                      {!logDetail.emailStatus && "送信待ち（またはメール未送信）"}
                    </Typography>
                    {logDetail.emailSentAt?.toDate && (
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(0,0,0,0.6)", mt: 0.3 }}>
                        送信日時: {logDetail.emailSentAt.toDate().toLocaleString("ja-JP")}
                      </Typography>
                    )}
                    {logDetail.emailTo && (
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(0,0,0,0.6)" }}>
                        送信先: {logDetail.emailTo}
                      </Typography>
                    )}
                    {logDetail.emailStatus === "failed" && logDetail.emailError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ wordBreak: "break-word" }}>
                          {logDetail.emailError}
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      {/* ───── コメントダイアログ ───── */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => setCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: textBase }}>
            コメント一覧
          </Typography>
          {commentDialogPost && (
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)", display: "block", mt: 0.5 }}>
              {commentDialogPost.userName} の投稿: 「{commentDialogPost.text}」
            </Typography>
          )}
          <IconButton
            onClick={() => setCommentDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingComments && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loadingComments && comments.length === 0 && (
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.5)", textAlign: "center", py: 3 }}>
              コメントはありません。
            </Typography>
          )}
          {!loadingComments && comments.length > 0 && (
            <Stack spacing={2}>
              {comments.map((c) => (
                <Box key={c.id}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar src={c.userAvatar || ""} sx={{ width: 32, height: 32, mt: 0.3 }}>
                      {(c.userName || "?")[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textBase, fontSize: 13 }}>
                          {c.userName || "名前なし"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.4)" }}>
                          {c.createdAt?.toDate
                            ? c.createdAt.toDate().toLocaleString("ja-JP")
                            : ""}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.3, color: textBase, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {c.text}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <FavoriteIcon sx={{ fontSize: 13, color: "rgba(0,0,0,0.3)" }} />
                        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.45)" }}>
                          {c.likeCount || 0}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
